// Who is signed in, and whose data this device holds (docs/SECURITY.md: personal accounts).
//
// Several people may use the same browser one after the other. What the device keeps belongs to
// a user: every queued change and every crash-recovery copy of a recording carries the id of the
// user who made it (`userId`, see actingUserId). Work queued by an older version has no such tag;
// it belongs to the *device owner*, the user who was signed in when it was written (cache key
// OWNER_KEY). Without an owner record it is from before user accounts and anyone may send it.
//
//  * Signing in as the owner changes nothing.
//  * Signing in as someone else drops the offline copies and the refused-changes list at once. The
//    previous user's typed drafts stay (keyed by user, only their author's pages read them). When
//    the device still holds another user's unsent work, nothing of it is shown or sent (adoptUser
//    resolves 'foreign'): that user signs in again to send it, or the new user discards it
//    (discardForeignData, which removes that user's drafts too).
//  * The outbox asks the gate (installSessionGate) before every request, so queued work is never
//    sent with another user's session – also when the cookie changed in another tab.
//  * Whose work it is must be known for sure: storage that cannot be read (or has not answered
//    yet) never counts as "nothing here" – the check is repeated instead.
import type { Features, MeResponse, User } from '../../../shared/types';
import {
  countManualDrafts,
  countOtherManualDrafts,
  legacyManualDrafts,
  removeOtherManualDrafts,
  writeManualDraft,
} from './drafts';
import {
  cacheDelete,
  cacheGetStrict,
  cachePut,
  clearContentCache,
  deleteRecordingData,
  listRecordingRecords,
  recordingsInUse,
  type RecordingRecord,
} from './localdb';
import {
  clearAccepted,
  clearFailed,
  discardOps,
  flush,
  listAllOpsStrict,
  listOps,
  logoutQueuedState,
  parkedUploads,
  queuedRecoveryIds,
  setSessionGate,
  subscribeSessionChanged,
  type GateVerdict,
  type OutboxOp,
  type ParkedUpload,
} from './outbox';

/** Cache key of the device owner (not removed by clearContentCache). */
export const OWNER_KEY = 'device:owner';

export interface DeviceOwner {
  id: string;
  /** Shown when another person signs in ("Neposlane spremembe uporabnika Ana Novak"). */
  name: string;
}

/** What this tab knows about the session the browser sends. */
type Known =
  | { kind: 'open' }
  | { kind: 'signedOut' }
  | { kind: 'user'; userId: string; restricted: boolean };

let known: Known | undefined;
/** Memory copy of the stored owner (undefined: not read yet). */
let ownerCopy: DeviceOwner | null | undefined;
/** The user this tab works for: new queued changes and recordings are tagged with it. */
let acting: string | null = null;
let refetch: (() => Promise<unknown>) | null = null;
let learning: Promise<void> | null = null;
const droppedListeners = new Set<() => void>();
const userListeners = new Set<(userId: string | null) => void>();

/** Called after the offline copies were removed because another user signed in. */
export function onViewDataDropped(listener: () => void): () => void {
  droppedListeners.add(listener);
  return () => {
    droppedListeners.delete(listener);
  };
}

/**
 * Notified when the session of this tab changes hands: the new user's id, or null once nobody is
 * signed in any more (logout here or in another tab, an ended session). Returns unsubscribe.
 */
export function onSessionUser(listener: (userId: string | null) => void): () => void {
  userListeners.add(listener);
  return () => {
    userListeners.delete(listener);
  };
}

/**
 * The user this tab works for – the one it shows as signed in, also offline and after the session
 * ended (until someone else signs in) – or null without accounts. New local work is tagged with it.
 */
export function actingUserId(): string | null {
  return acting;
}

/** The app runs from cached features (offline): the user they show works here. */
export function noteCachedUser(userId: string | null): void {
  if (userId) acting = userId;
}

function isOwner(value: unknown): value is DeviceOwner {
  return typeof value === 'object' && value !== null && typeof (value as DeviceOwner).id === 'string';
}

/**
 * The stored device owner (read from storage, so a change made by another tab is seen); null when
 * none is recorded. Rejects when storage cannot be read or has not answered yet.
 */
export async function readOwner(): Promise<DeviceOwner | null> {
  const value = await cacheGetStrict<unknown>(OWNER_KEY);
  ownerCopy = isOwner(value) ? { id: value.id, name: typeof value.name === 'string' ? value.name : '' } : null;
  return ownerCopy;
}

async function writeOwner(user: Pick<User, 'id' | 'displayName' | 'username'>): Promise<void> {
  ownerCopy = { id: user.id, name: user.displayName || user.username };
  await cachePut(OWNER_KEY, ownerCopy);
}

const userOf = (k: Known | undefined): string | null => (k?.kind === 'user' ? k.userId : null);
const sendable = (k: Known | undefined): boolean => k?.kind === 'open' || (k?.kind === 'user' && !k.restricted);

function setKnown(next: Known): void {
  const before = known;
  known = next;
  if (next.kind === 'user') acting = next.userId;
  const userId = userOf(next);
  const changedHands = before === undefined || userOf(before) !== userId || before.kind !== next.kind;
  if (changedHands) {
    for (const listener of [...userListeners]) {
      try {
        listener(userId);
      } catch (err) {
        console.error('[session] listener failed', err);
      }
    }
  }
  // Allowed now where it was not before, or another user's: send what is waiting (the gate
  // checks each op).
  if (sendable(next) && (!sendable(before) || changedHands)) flush();
}

/** A fresh (not cached) /api/features answer: who the session belongs to. */
export function noteFeatures(features: Features): void {
  if (!features.authenticated) setKnown({ kind: 'signedOut' });
  // Signed in without an account: no login at all, or a server from before user accounts.
  else if (!features.user) setKnown({ kind: 'open' });
  else setKnown({ kind: 'user', userId: features.user.id, restricted: features.restricted });
}

/** A login / setup in this tab succeeded. */
export function noteSignedIn(me: MeResponse): void {
  setKnown({ kind: 'user', userId: me.user.id, restricted: me.restricted });
}

/** Logged out in this tab. */
export function noteSignedOut(): void {
  setKnown({ kind: 'signedOut' });
}

/** The user finished the password change / 2FA setup (the session is no longer restricted). */
export function noteUnrestricted(userId: string): void {
  setKnown({ kind: 'user', userId, restricted: false });
}

/** Whose a piece of local work is: its tag, else (older versions) the device owner; null: anyone's. */
function workUser(tag: unknown, owner: DeviceOwner | null): string | null {
  return typeof tag === 'string' && tag !== '' ? tag : (owner?.id ?? null);
}

async function verdictFor(k: Known, op: OutboxOp): Promise<GateVerdict> {
  switch (k.kind) {
    case 'open':
      return 'ok';
    case 'signedOut':
      // The request would only be refused (401); the login page is shown meanwhile.
      return 'hold';
    case 'user': {
      if (k.restricted) return 'hold';
      if (op.userId !== undefined) return op.userId === k.userId ? 'ok' : 'hold';
      // Queued by a version that did not tag its work: the device owner's (rejects if unreadable).
      const owner = ownerCopy === undefined ? await readOwner() : ownerCopy;
      // No owner recorded: data from before user accounts – the user may send it.
      return owner === null || owner.id === k.userId ? 'ok' : 'hold';
    }
  }
}

async function learn(): Promise<void> {
  if (!refetch) return;
  learning ??= refetch()
    .then(
      () => undefined,
      () => undefined,
    )
    .finally(() => {
      learning = null;
    });
  await learning;
}

/**
 * Install the outbox gate. `refresh` re-reads /api/features (and calls noteFeatures with a fresh
 * answer); it is used when this tab does not know the session (yet, or any more).
 */
export function installSessionGate(refresh: () => Promise<unknown>): () => void {
  refetch = refresh;
  setSessionGate({
    async check(op) {
      if (known === undefined) await learn();
      // Still unknown: the server is not reachable (offline features carry no session).
      if (known === undefined) return 'retry';
      return verdictFor(known, op);
    },
    invalidate() {
      known = undefined;
      ownerCopy = undefined;
    },
  });
  const unsubscribe = subscribeSessionChanged(() => {
    known = undefined;
    ownerCopy = undefined;
  });
  return () => {
    unsubscribe();
    setSessionGate(null);
    refetch = null;
  };
}

/** Unsent work of a user on this device. */
export interface LocalWork {
  /** Queued changes other than recordings. */
  changes: number;
  /** Recordings the server does not have yet: queued and refused uploads, unsaved recordings. */
  recordings: number;
  /** Their total length in ms, as far as it is known. */
  recordedMs: number;
  /** Recordings still running (in another tab): they stay on the device, whatever is chosen. */
  recordingNow: number;
  /** Typed inspection entries that were not added yet (see drafts.ts). */
  drafts: number;
}

export type Ownership = { status: 'ok' } | { status: 'foreign'; previous: DeviceOwner; work: LocalWork };

const noWork = (): LocalWork => ({ changes: 0, recordings: 0, recordedMs: 0, recordingNow: 0, drafts: 0 });

/** A queued upload of a recording (also one the server refused, kept for recovery). */
const isRecordingOp = (op: OutboxOp) => op.kind === 'createAudioClip' || op.rejected !== undefined;

/** The recorded length sent with an upload (ms; 0 when unknown). */
function uploadMs(op: OutboxOp): number {
  const ms = typeof op.body === 'object' && op.body !== null ? (op.body as { durationMs?: unknown }).durationMs : undefined;
  return typeof ms === 'number' && Number.isFinite(ms) && ms > 0 ? ms : 0;
}

/**
 * Counts `ops` and recovery `records` into `work`. A record a queued upload refers to is part of
 * that upload; a record in `inUse` is still being recorded.
 */
function countWork(work: LocalWork, ops: OutboxOp[], records: RecordingRecord[], inUse: ReadonlySet<string>): void {
  const queued = new Set(ops.map((op) => op.recoveryId).filter((id): id is string => typeof id === 'string'));
  for (const op of ops) {
    if (!isRecordingOp(op)) {
      work.changes++;
      continue;
    }
    work.recordings++;
    work.recordedMs += uploadMs(op);
  }
  for (const r of records) {
    if (queued.has(r.id)) continue;
    if (inUse.has(r.id)) work.recordingNow++;
    else if (r.bytes > 0) {
      work.recordings++;
      work.recordedMs += Math.max(0, r.durationMs);
    }
  }
}

/** Unsent work of other users than `userId` (and who they are). Rejects when it cannot be read. */
async function foreignWork(userId: string, owner: DeviceOwner | null): Promise<{ work: LocalWork; users: string[] }> {
  const [ops, records] = await Promise.all([listAllOpsStrict(), listRecordingRecords({ strict: true })]);
  const users = new Set<string>();
  const foreign = (tag: unknown) => {
    const who = workUser(tag, owner);
    if (who === null || who === userId) return false;
    users.add(who);
    return true;
  };
  const work = noWork();
  // A recording of another user that is still running (in another tab) counts like any other of theirs.
  countWork(
    work,
    ops.filter((op) => foreign(op.userId)),
    records.filter((r) => r.bytes > 0 && foreign(r.meta.userId)),
    new Set(),
  );
  // Shown when the page lists that user's data; on their own they never block (only their author sees them).
  work.drafts = countOtherManualDrafts(userId);
  return { work, users: [...users] };
}

/**
 * Unsent work of `userId` on this device (for the logout dialog). Best effort: what cannot be read
 * is not counted.
 */
export async function ownLocalWork(userId: string | null): Promise<LocalWork> {
  const [ops, parked, records, owner, queued, inUse] = await Promise.all([
    listOps().catch(() => [] as OutboxOp[]),
    parkedUploads().catch(() => [] as ParkedUpload[]),
    listRecordingRecords().catch(() => [] as RecordingRecord[]),
    readOwner().catch(() => null),
    queuedRecoveryIds().catch(() => new Set<string>()),
    recordingsInUse().catch(() => new Set<string>()),
  ]);
  const own = (tag: unknown) => {
    const who = workUser(tag, owner);
    return who === null || who === userId;
  };
  const work = noWork();
  countWork(
    work,
    [...ops, ...parked.map((p) => p.op)].filter((op) => own(op.userId)),
    // Copies the queue still refers to (also of ops of another tab not listed here) belong to their upload.
    records.filter((r) => own(r.meta.userId) && !queued.has(r.id)),
    inUse,
  );
  work.drafts = countManualDrafts(userId);
  return work;
}

/**
 * Deletes the unsent work of `userId` from this device: queued changes, refused uploads and
 * recordings – not one that is being recorded right now (in another tab). Resolves how many such
 * running recordings were kept. Rejects – deleting nothing – when the device owner cannot be read
 * (untagged work could be someone else's).
 */
export async function discardOwnWork(userId: string | null): Promise<number> {
  const owner = await readOwner();
  const own = (tag: unknown) => {
    const who = workUser(tag, owner);
    return who === null || who === userId;
  };
  await discardOps((op) => own(op.userId));
  const inUse = await recordingsInUse();
  let kept = 0;
  for (const r of await listRecordingRecords()) {
    if (!own(r.meta.userId)) continue;
    if (inUse.has(r.id)) kept++;
    else await deleteRecordingData(r.id).catch(() => {});
  }
  return kept;
}

/**
 * Drops what is kept on this device for display only: offline copies and the recently accepted
 * changes; with `failed`, the list of refused changes too (it holds the refused text, the only copy
 * of it: kept when a session merely ended, for its user who signs in again). Typed drafts and
 * unsent work stay. Resolves false when the stored copies could not be removed.
 */
export async function dropViewData(opts: { failed: boolean }): Promise<boolean> {
  for (const listener of [...droppedListeners]) listener();
  clearAccepted();
  const cleared = await clearContentCache();
  await cacheDelete('settings');
  if (opts.failed) await clearFailed().catch(() => {});
  return cleared;
}

/**
 * `user` is signed in on this device: make it the device owner. The offline copies of another
 * user are removed first; when another user's unsent work is still here, resolves 'foreign' and
 * leaves it as it is (see discardForeignData). Rejects when storage cannot be read, or cannot say
 * yet whether this device logged out (the caller checks again later; nothing is shown or sent
 * meanwhile).
 */
export async function adoptUser(user: User): Promise<Ownership> {
  // A logout the server has not heard of: the features read that follows signs the device out.
  if ((await logoutQueuedState()) !== 'no') throw new Error('Whether this device logged out is not known yet.');
  const owner = await readOwner();
  if (owner?.id !== user.id) {
    // Someone else's (or, without an owner, an older version's) offline copies: never shown here.
    // Drafts typed before accounts existed are kept for this user, like that version's queued work.
    const legacy = owner === null ? legacyManualDrafts() : new Map<string, string>();
    if (!(await dropViewData({ failed: true }))) throw new Error('The offline copies could not be removed.');
    for (const [docId, text] of legacy) {
      writeManualDraft(user.id, docId, text);
      writeManualDraft(null, docId, '');
    }
  }
  const { work, users } = await foreignWork(user.id, owner);
  if (work.changes > 0 || work.recordings > 0) {
    const previous = owner && users.includes(owner.id) ? owner : { id: users[0]!, name: '' };
    return { status: 'foreign', previous, work };
  }
  if (owner?.id !== user.id || owner.name !== (user.displayName || user.username)) await writeOwner(user);
  flush();
  return { status: 'ok' };
}

/** Deletes the other users' unsent work and makes `user` the device owner. */
export async function discardForeignData(user: User): Promise<void> {
  const owner = await readOwner();
  const foreign = (tag: unknown) => {
    const who = workUser(tag, owner);
    return who !== null && who !== user.id;
  };
  await discardOps((op) => foreign(op.userId));
  for (const r of await listRecordingRecords({ strict: true })) {
    if (foreign(r.meta.userId)) await deleteRecordingData(r.id).catch(() => {});
  }
  await dropViewData({ failed: true });
  removeOtherManualDrafts(user.id);
  await writeOwner(user);
  flush();
}

/** Tests: forget the in-memory state. */
export function resetSessionState(): void {
  known = undefined;
  ownerCopy = undefined;
  acting = null;
  learning = null;
}
